import { Link } from "@tanstack/react-router";
import { useMe } from "../../lib/me";
import { cx } from "../../ui";
import { Icon, IconButton, type IconName } from "../../ui/icons";
import { allCountWithout, useHiddenApps, type HiddenApps } from "../inbox/hidden";
import { KIND_VIEWS, mailKindOf, type MailKindView } from "../inbox/kinds";
import { BOXES, boxOf, type Box } from "../inbox/mailboxes";
import { InboxSearch } from "../inbox/scope";
import { allCount, useKindCounts, type KindCounts } from "../inbox/use-kind-counts";
import { useScope } from "../inbox/use-scope";

const boxClass =
  "flex min-h-9 items-center gap-3 rounded-md text-sm text-text hover:bg-surface-2 aria-[current=page]:bg-surface-2 aria-[current=page]:font-semibold aria-[current=page]:text-accent";

/** What the Inbox group can open: a kind, GitHub (WP-062) or All mail. */
type InboxView = MailKindView | "github";

interface InboxEntry {
  view: InboxView;
  label: string;
  icon: IconName | null;
  /** GitHub sits under Notifications. */
  nested: boolean;
}

/** The Inbox group, in the sidebar's order (architecture §6.7, WP-064, WP-065): All mail first. */
export const INBOX_ENTRIES: readonly InboxEntry[] = [
  { view: "all", label: "All mail", icon: "unread", nested: false },
  ...KIND_VIEWS.flatMap((k): InboxEntry[] => {
    const entry: InboxEntry = { view: k.key, label: k.label, icon: k.icon, nested: false };
    return k.key === "notification" ? [entry, { view: "github", label: "GitHub", icon: null, nested: true }] : [entry];
  }),
];

/**
 * An entry's unread count: All mail is the kinds' sum, not a query of its own, less the apps kept
 * out of it (WP-066; GitHub is the only one).
 */
export function countOf(view: InboxView, counts: KindCounts, hidden: HiddenApps = []): number | null {
  if (view !== "all") return counts[view];
  return allCountWithout(allCount(counts), hidden.includes("github") ? [counts.github] : []);
}

function searchOf(view: InboxView, scope: string | undefined) {
  if (view === "github") return { scope, app: "github" as const };
  return { scope, kind: view === "all" ? undefined : view };
}

/**
 * The sidebar on Mail (WP-056, WP-064, WP-065): the Inbox group (All mail, People, Notifications
 * with GitHub under it, Newsletters, Receipts), each with its unread count in the current scope when above
 * zero, then the other mailboxes. Collapsed to a rail from `md`, the Inbox group is the Inbox icon
 * only (the phone drawer still shows it all) and the mailboxes are icons.
 */
export function MailNav({ workspaceId, search, collapsed }: { workspaceId: string; search: unknown; collapsed: boolean }) {
  const me = useMe();
  const parsed = InboxSearch.safeParse(search);
  const current = parsed.success ? parsed.data : {};
  const box = boxOf(current);
  // A mailbox or kind keeps the scope (workspace, all, an account or a saved scope); the rest starts fresh.
  const scope = "scope" in current ? current.scope : undefined;
  const inSource = "app" in current && current.app !== undefined;
  const shown: InboxView | null = box !== "inbox" ? null : inSource ? "github" : mailKindOf("kind" in current ? current.kind : undefined);
  const { ids } = useScope(workspaceId, scope);
  const counts = useKindCounts(me.uid, ids, true);
  const { hidden, setHidden } = useHiddenApps(me.uid);
  const hide = collapsed ? "md:sr-only" : "";
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-3">
      <nav aria-label="Inbox" className="flex flex-col gap-0.5">
        <h2 className={cx("px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted", hide)}>Inbox</h2>
        {collapsed ? (
          <Link
            to="/w/$workspaceId/inbox"
            params={{ workspaceId }}
            search={{ scope }}
            aria-current={shown !== null ? "page" : undefined}
            activeOptions={{ exact: true, includeSearch: true }}
            title="Inbox"
            className={cx(boxClass, "hidden px-0 md:flex md:justify-center")}
          >
            <Icon name="inbox" />
            <span className="sr-only">Inbox</span>
          </Link>
        ) : null}
        {INBOX_ENTRIES.map((e) => {
          const n = countOf(e.view, counts, hidden);
          const unread = n !== null && n > 0 ? n : null;
          // GitHub can be kept out of All mail (WP-066): an eye button on its row, shown on hover
          // or focus, and kept in sight (crossed out) while it is hidden.
          const hideable = e.view === "github";
          const off = hideable && hidden.includes("github");
          const label = `${e.label}${unread === null ? "" : `, ${unread} unread`}${off ? ", hidden from All mail" : ""}`;
          const link = (
            <Link
              key={e.view}
              to="/w/$workspaceId/inbox"
              params={{ workspaceId }}
              search={searchOf(e.view, scope)}
              aria-current={shown === e.view ? "page" : undefined}
              activeOptions={{ exact: true, includeSearch: true }}
              aria-label={label}
              className={cx(boxClass, e.nested ? "pl-12 pr-3" : "px-3", hideable && "pr-10", off && "text-muted", collapsed && "md:hidden")}
            >
              {e.icon === null ? null : <Icon name={e.icon} />}
              <span className="min-w-0 flex-1 truncate">{e.label}</span>
              {unread === null ? null : (
                <span aria-hidden="true" className={cx("shrink-0 text-xs font-semibold tabular-nums", off ? "text-muted" : "text-text")}>
                  {unread}
                </span>
              )}
            </Link>
          );
          if (!hideable) return link;
          return (
            <div key={e.view} className={cx("group relative flex flex-col", collapsed && "md:hidden")}>
              {link}
              <IconButton
                icon={off ? "eyeOff" : "eye"}
                label={off ? "Show GitHub in All mail" : "Hide GitHub from All mail"}
                aria-pressed={off}
                onClick={() => setHidden("github", !off)}
                className={cx(
                  "absolute right-1 top-1/2 size-7 -translate-y-1/2",
                  off ? "opacity-100" : "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100",
                )}
              />
            </div>
          );
        })}
      </nav>
      <nav aria-label="Mailboxes" className="flex flex-col gap-0.5">
        <h2 className={cx("px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted", hide)}>Mailboxes</h2>
        {BOXES.filter((b) => b.key !== "inbox").map((b) => (
          <Link
            key={b.key}
            to="/w/$workspaceId/inbox"
            params={{ workspaceId }}
            search={{ scope, box: b.key as Box }}
            aria-current={b.key === box ? "page" : undefined}
            // The router marks a link current only on an exact match (the same mailbox, nothing open);
            // `aria-current` above also covers an open conversation or an old `filter` link.
            activeOptions={{ exact: true, includeSearch: true }}
            title={b.label}
            className={cx(boxClass, collapsed ? "px-3 md:justify-center md:px-0" : "px-3")}
          >
            <Icon name={b.icon} />
            <span className={hide}>{b.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
